import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, X, AlertCircle, ImagePlus } from 'lucide-react';
import api, { API_BASE_URL } from '../api';

/**
 * Visitor-attached images, shared by every chat surface: ChatEmbedWidget (Chat
 * widget, Combo Widget V2, Unified combo) and the Avatar Panels.
 *
 * Images upload as soon as they are picked, pasted or dropped, so pressing Send
 * only has to pass the returned ids along. The backend re-encodes them, strips
 * EXIF, and gates the whole feature on the agent's chat_image_upload_enabled.
 */

export const CHAT_IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
// Per message. The org's real limit comes from agent.chat_image_limits.per_message
// (set by super admin); this is the default when it is absent.
export const MAX_CHAT_IMAGES = 2;
const MAX_CHAT_IMAGE_BYTES = 5 * 1024 * 1024; // matches chat_images.MAX_UPLOAD_BYTES
const ACCEPTED_TYPES = new Set(CHAT_IMAGE_ACCEPT.split(','));

/** Absolute src for an image url returned by the upload endpoint. */
export const chatImageSrc = (url) => (url && url.startsWith('/') ? `${API_BASE_URL}${url}` : url);

/** Image files out of a paste or drop event's DataTransfer. */
export const imageFilesFrom = (dataTransfer) => {
    if (!dataTransfer) return [];
    return Array.from(dataTransfer.files || []).filter((f) => f.type && f.type.startsWith('image/'));
};

const uploadChatImage = async (agentId, file, sessionId) => {
    const formData = new FormData();
    formData.append('file', file);
    // Lets the API hold a conversation to its per-session image cap.
    if (sessionId) formData.append('session_id', sessionId);
    const response = await api.post(`/agents/${agentId}/chat/embed/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { id: response.data.image_id, url: response.data.url };
};

let localSeq = 0;

/**
 * Pending attachments for one composer.
 *
 * `ready` is what a send should carry ({id, url} pairs); `uploading` blocks the
 * send button until every picked image has an id.
 */
export const useChatImageAttachments = (agentId, getSessionId, maxImages = MAX_CHAT_IMAGES) => {
    const [items, setItems] = useState([]);
    const [error, setError] = useState(null);
    const itemsRef = useRef(items);
    itemsRef.current = items;
    const getSessionIdRef = useRef(getSessionId);
    getSessionIdRef.current = getSessionId;
    const maxRef = useRef(maxImages);
    maxRef.current = maxImages || MAX_CHAT_IMAGES;

    // Preview object URLs are revoked when an item leaves and on unmount.
    useEffect(() => () => {
        itemsRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    }, []);

    const addFiles = useCallback((fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length || !agentId) return;
        setError(null);

        const max = maxRef.current;
        const room = max - itemsRef.current.length;
        if (room <= 0) {
            setError(`You can attach up to ${max} images per message`);
            return;
        }
        if (files.length > room) setError(`You can attach up to ${max} images per message`);

        files.slice(0, room).forEach((file) => {
            if (!ACCEPTED_TYPES.has(file.type)) {
                setError('Please attach a JPG, PNG, WebP or GIF image');
                return;
            }
            if (file.size > MAX_CHAT_IMAGE_BYTES) {
                setError('Image too large. Maximum size is 5MB');
                return;
            }
            const localId = `img-${Date.now()}-${localSeq++}`;
            const previewUrl = URL.createObjectURL(file);
            setItems((prev) => [...prev, { localId, previewUrl, status: 'uploading' }]);
            uploadChatImage(agentId, file, getSessionIdRef.current?.())
                .then(({ id, url }) => {
                    setItems((prev) => prev.map((it) => (it.localId === localId ? { ...it, status: 'ready', id, url } : it)));
                })
                .catch((err) => {
                    setError(err.response?.data?.detail || 'Could not upload the image');
                    setItems((prev) => prev.map((it) => (it.localId === localId ? { ...it, status: 'error' } : it)));
                });
        });
    }, [agentId]);

    const remove = useCallback((localId) => {
        setItems((prev) => {
            const gone = prev.find((it) => it.localId === localId);
            if (gone) URL.revokeObjectURL(gone.previewUrl);
            return prev.filter((it) => it.localId !== localId);
        });
        setError(null);
    }, []);

    /** Empties the composer and returns what was ready, for the message being sent. */
    const take = useCallback(() => {
        const ready = itemsRef.current.filter((it) => it.status === 'ready').map(({ id, url }) => ({ id, url }));
        itemsRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
        setItems([]);
        setError(null);
        return ready;
    }, []);

    /** Puts already-uploaded images back in the composer, e.g. after a failed send. */
    const restore = useCallback((images) => {
        if (!images?.length) return;
        setItems((prev) => [
            ...prev,
            ...images.map((img) => ({
                localId: `img-${Date.now()}-${localSeq++}`,
                previewUrl: chatImageSrc(img.url),
                status: 'ready',
                id: img.id,
                url: img.url,
                restored: true,
            })),
        ]);
    }, []);

    return {
        items,
        error,
        setError,
        addFiles,
        remove,
        take,
        restore,
        readyCount: items.filter((it) => it.status === 'ready').length,
        uploading: items.some((it) => it.status === 'uploading'),
    };
};

/** Thumbnails of the images waiting to be sent, above the text box. */
export const ChatAttachmentStrip = ({ attachments, isDarkMode, accentColor = '#6366f1' }) => {
    const { items, error, remove } = attachments;
    if (!items.length && !error) return null;
    return (
        <div className="mb-2">
            {items.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                    {items.map((item) => (
                        <div
                            key={item.localId}
                            className="relative w-14 h-14 rounded-lg overflow-hidden flex-shrink-0"
                            style={{ border: `1px solid ${item.status === 'error' ? '#ef4444' : (isDarkMode ? '#374151' : '#e5e7eb')}` }}
                        >
                            <img
                                src={item.previewUrl}
                                alt="Attachment"
                                className="w-full h-full object-cover"
                                style={{ opacity: item.status === 'ready' ? 1 : 0.5 }}
                            />
                            {item.status === 'uploading' && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: accentColor }} />
                                </div>
                            )}
                            {item.status === 'error' && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <AlertCircle className="w-4 h-4" style={{ color: '#ef4444' }} />
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={() => remove(item.localId)}
                                aria-label="Remove image"
                                className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: 'rgba(17,24,39,0.75)', color: '#ffffff' }}
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {error && <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{error}</p>}
        </div>
    );
};

/** Images inside a sent message bubble. Clicking one opens it full size. */
export const ChatMessageImages = ({ images }) => {
    if (!images?.length) return null;
    const one = images.length === 1;
    return (
        <div className={`grid gap-1 mb-1 ${one ? 'grid-cols-1' : 'grid-cols-2'}`} style={{ maxWidth: one ? 220 : 240 }}>
            {images.map((img) => (
                <a
                    key={img.id || img.url}
                    href={chatImageSrc(img.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-md overflow-hidden"
                >
                    <img
                        src={chatImageSrc(img.url)}
                        alt="Shared"
                        loading="lazy"
                        className="w-full object-cover"
                        style={{ maxHeight: one ? 220 : 110, minHeight: 40, backgroundColor: 'rgba(0,0,0,0.06)' }}
                    />
                </a>
            ))}
        </div>
    );
};

// Mirrors TEXT_ONLY_MODELS in app/api/llm_models.py.
const TEXT_ONLY_MODELS = new Set(['gpt-4', 'gpt-3.5-turbo']);
export const modelSupportsImages = (model) => {
    const m = String(model || 'gpt-4o-mini');
    return !TEXT_ONLY_MODELS.has(m) && !m.startsWith('gpt-3.5');
};

/**
 * The agent-level switch, shown in each widget's settings (Chat, Combo Widget
 * V2, Avatar Panel). One column backs all of them: it governs every chat
 * surface of the agent at once.
 */
export const ChatImageSharingSetting = ({ enabled, onChange, llmModel, Icon }) => {
    const unsupported = !modelSupportsImages(llmModel);
    return (
        <div className="space-y-3">
            <label className="text-sm font-semibold text-foreground flex items-center gap-2">
                {Icon && <Icon className="w-4 h-4" />}
                Image Sharing
            </label>
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    role="switch"
                    aria-checked={enabled}
                    onClick={() => onChange(!enabled)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                        enabled ? 'bg-primary' : 'bg-muted'
                    }`}
                >
                    <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            enabled ? 'translate-x-5' : 'translate-x-1'
                        }`}
                    />
                </button>
                <span className="text-sm text-muted-foreground">
                    {enabled ? 'Enabled' : 'Disabled'} – Let visitors attach images in chat
                </span>
            </div>
            {enabled && String(llmModel || 'gpt-4o-mini') === 'gpt-4o-mini' && (
                <p className="text-xs text-amber-600">
                    GPT-4o Mini bills each image at about 8,500 input tokens, roughly 40 times more than GPT-4.1 Mini or GPT-5 Mini, which read images just as well. That also slows the reply after an image, most noticeably on Avatar Panel calls.
                </p>
            )}
            {enabled && unsupported && (
                <p className="text-xs text-amber-600">
                    This agent's model ({llmModel}) can't read images, so visitors won't see the attach button. Switch to a vision model such as GPT-4o, GPT-4.1, GPT-5, Claude or Gemini.
                </p>
            )}
            <p className="text-xs text-muted-foreground">
                Applies to every chat surface of this agent: Chat widget, Combo Widget, and the Avatar Panel text box. Images are resized, stripped of location data, and deleted along with the conversation.
            </p>
        </div>
    );
};

// ── Avatar Panel (live call) helpers ─────────────────────────────────────────

/** LiveKit text-stream topic the voice agent reads image messages from (app/agent/chat_image_input.py). */
export const CHAT_IMAGE_TOPIC = 'bcx.chat.image';

/** {id, url} pairs for image ids that came back over the data channel. */
export const chatImagesFromIds = (agentId, ids) =>
    (Array.isArray(ids) ? ids : []).map((id) => ({ id, url: `/agents/${agentId}/chat/embed/images/${id}` }));

const IMAGE_MARKER_RE = /^\[(?:Image attached|\d+ images attached)\]\s*/;

/**
 * The transcript line the agent records for a message with images. The panel
 * echoes the same text so its optimistic bubble and the agent's copy dedupe.
 */
export const imageTranscriptText = (text, ids) => {
    if (!ids?.length) return text;
    const marker = ids.length === 1 ? '[Image attached]' : `[${ids.length} images attached]`;
    return text ? `${marker} ${text}` : marker;
};

/** Caption text with the marker removed, for a bubble that shows the images themselves. */
export const stripImageMarker = (text) => String(text || '').replace(IMAGE_MARKER_RE, '');

// ── Drag and drop ────────────────────────────────────────────────────────────

/**
 * Drop target for image files. Spread `dropHandlers` on the container and
 * render <ChatDropOverlay> inside it (the container must be positioned).
 * Only file drags react, so dragging text or links inside the page is untouched.
 */
export const useImageDrop = (enabled, onFiles) => {
    const [dragActive, setDragActive] = useState(false);
    if (!enabled) return { dragActive: false, dropHandlers: {} };
    const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
    return {
        dragActive,
        dropHandlers: {
            onDragOver: (e) => {
                if (!isFileDrag(e)) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
                if (!dragActive) setDragActive(true);
            },
            onDragLeave: (e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setDragActive(false);
            },
            onDrop: (e) => {
                setDragActive(false);
                const files = imageFilesFrom(e.dataTransfer);
                if (!files.length) return;
                e.preventDefault();
                onFiles(files);
            },
        },
    };
};

export const ChatDropOverlay = ({ active, accentColor = '#6366f1', isDarkMode, radius = 0 }) => {
    if (!active) return null;
    return (
        <div
            style={{
                position: 'absolute',
                inset: 0,
                zIndex: 30,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                pointerEvents: 'none',
                borderRadius: radius,
                background: isDarkMode ? 'rgba(17,24,39,0.85)' : 'rgba(255,255,255,0.88)',
                border: `2px dashed ${accentColor}`,
                color: accentColor,
            }}
        >
            <ImagePlus size={30} />
            <span style={{ fontSize: 14, fontWeight: 600 }}>Drop images to attach</span>
        </div>
    );
};

/**
 * The per-organization limits a super admin can override (orgs.chat_image_limits).
 * Defaults and ranges mirror DEFAULT_LIMITS / LIMIT_BOUNDS in app/api/chat_images.py,
 * which clamps whatever is sent; a blank field means "use the default".
 */
export const CHAT_IMAGE_LIMIT_FIELDS = [
    { key: 'per_message', label: 'Images per message', fallback: 2, min: 1, max: 10 },
    { key: 'per_conversation', label: 'Images per conversation / call', fallback: 10, min: 1, max: 200 },
    { key: 'per_minute', label: 'Uploads per minute (per visitor)', fallback: 5, min: 1, max: 120 },
    { key: 'per_day', label: 'Uploads per day (per visitor)', fallback: 30, min: 1, max: 1000 },
    { key: 'in_context', label: 'Images the AI sees per turn', fallback: 4, min: 1, max: 12 },
];
